import type { ReactNode } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { TENANT_ADMIN_ROLES, tenantHasModule, type UserRole } from '@kennisbank/shared';
import { LoginPage } from './pages/Login.js';
import { ProfilePage } from './pages/Profile.js';
import { SocialeKaartPage } from './pages/SocialeKaart.js';
import { DocumentenPage } from './pages/Documenten.js';
import { BeheerSocialeKaartPage } from './pages/BeheerSocialeKaart.js';
import { BeheerKennisbankPage } from './pages/BeheerKennisbank.js';
import { BeheerWelkomstpaneelPage } from './pages/BeheerWelkomstpaneel.js';
import { BeheerWijzigingsverzoekenPage } from './pages/BeheerWijzigingsverzoeken.js';
import { IncidentMeldenPage } from './pages/IncidentMelden.js';
import { BeheerIncidentenPage } from './pages/BeheerIncidenten.js';
import { DeclaratiesPage } from './pages/Declaraties.js';
import { BeheerDeclaratiesPage } from './pages/BeheerDeclaraties.js';
import { BeheerAuditLogPage } from './pages/BeheerAuditLog.js';
import { BeheerDemoToegangPage } from './pages/BeheerDemoToegang.js';
import { BeheerIntervisiePage } from './pages/BeheerIntervisie.js';
import { BeheerElearningPage } from './pages/BeheerElearning.js';
import { BeheerGebruikersPage } from './pages/BeheerGebruikers.js';
import { AiToolsGate } from './components/AiToolsGate.js';
import { HelpPage } from './pages/Help.js';
import { WatIsNieuwPage } from './pages/WatIsNieuw.js';
import { BeheerWatIsNieuwPage } from './pages/BeheerWatIsNieuw.js';
import { KennisagentPage } from './pages/Kennisagent.js';
import { NieuwsPage } from './pages/Nieuws.js';
import { AgendaPage } from './pages/Agenda.js';
import { AcademyZonderModulesPage, ELearningsPage } from './pages/ELearnings.js';
import { IntervisiePage } from './pages/Intervisie.js';
import { SmoelenboekPage } from './pages/Smoelenboek.js';
import { BerichtenPage } from './pages/Berichten.js';
import { SaraPage } from './pages/Sara.js';
import { VerwijsbrievenPage } from './pages/Verwijsbrieven.js';
import { UrenPage } from './pages/Uren.js';
import { BeheerTijdregistratiePage } from './pages/BeheerTijdregistratie.js';
import { HandigeLinksPage } from './pages/HandigeLinks.js';
import { EhealthIntegratiePage } from './pages/EhealthIntegratie.js';
import { AuthProvider, useAuth } from './auth/AuthProvider.js';
import { ShellProvider } from './components/Layout.js';
import { TenantBrandSync } from './lib/tenant-brand.js';
import { RequireAuth } from './auth/RequireAuth.js';
import { RoleGuard } from './auth/RoleGuard.js';
import { ModuleGuard } from './auth/ModuleGuard.js';
import { NietBeschikbaarPage } from './pages/NietBeschikbaar.js';

// Modules currently restricted to SUPPOHRT medewerkers and admins.
const SUPPOHRT_ROLES: UserRole[] = ['suppohrt_admin', 'suppohrt_medewerker'];

/**
 * Tenant-aware landing for "/". Rendered inside RequireAuth, so `user` is set.
 *
 * Previously "/" unconditionally redirected to /kennisagent, where the
 * RoleGuard bounced disallowed roles back to "/", which redirected straight
 * back to /kennisagent — an infinite loop ("Maximum update depth exceeded").
 */
function HomeRedirect(): JSX.Element {
  const { user } = useAuth();
  // Nieuws is de startpagina (besluit Jos 3 sep 2026, SharePoint-vervanger).
  // Een tenant zonder Nieuws landt op de Sociale Kaart, en zonder beide op Help
  // (geen modulepoort), anders stuurt de ModuleGuard eindeloos terug naar "/".
  const dest = tenantHasModule(user?.tenantId, 'nieuws')
    ? '/nieuws'
    : tenantHasModule(user?.tenantId, 'socialeKaart')
      ? '/sociale-kaart'
      : '/help';
  return <Navigate to={dest} replace />;
}

function VerwijsbriefGate({ children }: { children: ReactNode }): JSX.Element {
  const { user } = useAuth();
  return user?.verwijsbrief ? <>{children}</> : <Navigate to="/" replace />;
}

export function App(): JSX.Element {
  return (
    <AuthProvider>
      <TenantBrandSync />
      <ShellProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/"
            element={
              <RequireAuth>
                <HomeRedirect />
              </RequireAuth>
            }
          />

          {/* ── Kennistools ── */}
          <Route
            path="/kennisagent"
            element={
              <RequireAuth>
                <ModuleGuard module="mila">
                  <AiToolsGate>
                    <KennisagentPage />
                  </AiToolsGate>
                </ModuleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/sara"
            element={
              <RequireAuth>
                <ModuleGuard
                  module="sara"
                  fallback={
                    <NietBeschikbaarPage
                      titel="Sara"
                      omschrijving="Sara ondersteunt bij het uitwerken van gesprekken."
                    />
                  }
                >
                  <RoleGuard allowed={SUPPOHRT_ROLES}>
                    <AiToolsGate>
                      <SaraPage />
                    </AiToolsGate>
                  </RoleGuard>
                </ModuleGuard>
              </RequireAuth>
            }
          />
          {/* Verwijsbrieven (ADR 0017): alleen voor de e-mail-allowlist. */}
          {['/verwijsbrieven', '/verwijsbrieven/:id'].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <RequireAuth>
                  <ModuleGuard module="verwijsbrieven">
                    <VerwijsbriefGate>
                      <VerwijsbrievenPage />
                    </VerwijsbriefGate>
                  </ModuleGuard>
                </RequireAuth>
              }
            />
          ))}
          <Route
            path="/uren"
            element={
              <RequireAuth>
                <ModuleGuard module="uren">
                  <RoleGuard allowed={SUPPOHRT_ROLES}>
                    <UrenPage />
                  </RoleGuard>
                </ModuleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/documenten"
            element={
              <RequireAuth>
                <ModuleGuard module="documenten">
                  <DocumentenPage />
                </ModuleGuard>
              </RequireAuth>
            }
          />

          {/* ── Zorgverleners ── */}
          <Route
            path="/sociale-kaart"
            element={
              <RequireAuth>
                <ModuleGuard module="socialeKaart">
                  <SocialeKaartPage />
                </ModuleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/smoelenboek"
            element={
              <RequireAuth>
                <ModuleGuard module="smoelenboek">
                  <SmoelenboekPage />
                </ModuleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/e-health"
            element={
              <RequireAuth>
                <ModuleGuard module="eHealth">
                  <RoleGuard allowed={SUPPOHRT_ROLES}>
                    <EhealthIntegratiePage />
                  </RoleGuard>
                </ModuleGuard>
              </RequireAuth>
            }
          />

          {/* ── Scholing ── */}
          <Route
            path="/agenda"
            element={
              <RequireAuth>
                <ModuleGuard module="agenda">
                  <AgendaPage />
                </ModuleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/e-learnings"
            element={
              <RequireAuth>
                <ModuleGuard module="academy" fallback={<AcademyZonderModulesPage />}>
                  <RoleGuard allowed={SUPPOHRT_ROLES}>
                    <ELearningsPage />
                  </RoleGuard>
                </ModuleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/intervisie"
            element={
              <RequireAuth>
                <ModuleGuard
                  module="intervisie"
                  fallback={
                    <NietBeschikbaarPage
                      titel="Intervisie"
                      omschrijving="Bij Intervisie vind je je intervisiegroep en de sessies via Teams."
                    />
                  }
                >
                  <RoleGuard allowed={SUPPOHRT_ROLES}>
                    <IntervisiePage />
                  </RoleGuard>
                </ModuleGuard>
              </RequireAuth>
            }
          />
          {/* Groepsgesprekken is absorbed into the unified Berichten module (ADR 0004). */}
          <Route path="/groepsgesprekken" element={<Navigate to="/berichten" replace />} />

          {/* ── Informatie ── */}
          <Route
            path="/berichten"
            element={
              <RequireAuth>
                <ModuleGuard module="chats">
                  <BerichtenPage />
                </ModuleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/nieuws"
            element={
              <RequireAuth>
                <ModuleGuard module="nieuws">
                  <NieuwsPage />
                </ModuleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/incident-melden"
            element={
              <RequireAuth>
                <ModuleGuard module="incidenten">
                  <IncidentMeldenPage />
                </ModuleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/declaraties"
            element={
              <RequireAuth>
                <ModuleGuard module="declaraties">
                  <DeclaratiesPage />
                </ModuleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/handige-links"
            element={
              <RequireAuth>
                <ModuleGuard module="handigeLinks">
                  <RoleGuard allowed={SUPPOHRT_ROLES}>
                    <HandigeLinksPage />
                  </RoleGuard>
                </ModuleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/help"
            element={
              <RequireAuth>
                <HelpPage />
              </RequireAuth>
            }
          />

          {/* ── Beheer ── */}
          <Route
            path="/beheer/sociale-kaart"
            element={
              <RequireAuth>
                <RoleGuard allowed={['suppohrt_admin']}>
                  <BeheerSocialeKaartPage />
                </RoleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/beheer/gebruikers"
            element={
              <RequireAuth>
                <RoleGuard allowed={[...TENANT_ADMIN_ROLES]}>
                  <BeheerGebruikersPage />
                </RoleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/beheer/kennisbank"
            element={
              <RequireAuth>
                <RoleGuard allowed={['suppohrt_admin']}>
                  <BeheerKennisbankPage />
                </RoleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/beheer/welkomstpaneel"
            element={
              <RequireAuth>
                <RoleGuard allowed={[...TENANT_ADMIN_ROLES]}>
                  <BeheerWelkomstpaneelPage />
                </RoleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/wat-is-nieuw"
            element={
              <RequireAuth>
                <ModuleGuard module="watIsNieuw">
                  <WatIsNieuwPage />
                </ModuleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/beheer/wat-is-nieuw"
            element={
              <RequireAuth>
                <RoleGuard allowed={['suppohrt_admin']}>
                  <BeheerWatIsNieuwPage />
                </RoleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/beheer/incidenten"
            element={
              <RequireAuth>
                <ModuleGuard module="incidenten">
                  <RoleGuard allowed={['suppohrt_admin']}>
                    <BeheerIncidentenPage />
                  </RoleGuard>
                </ModuleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/beheer/declaraties"
            element={
              <RequireAuth>
                <ModuleGuard module="declaraties">
                  <RoleGuard allowed={SUPPOHRT_ROLES}>
                    <BeheerDeclaratiesPage />
                  </RoleGuard>
                </ModuleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/beheer/wijzigingsverzoeken"
            element={
              <RequireAuth>
                <RoleGuard allowed={['suppohrt_admin']}>
                  <BeheerWijzigingsverzoekenPage />
                </RoleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/beheer/demo-toegang"
            element={
              <RequireAuth>
                <BeheerDemoToegangPage />
              </RequireAuth>
            }
          />
          <Route
            path="/beheer/audit-log"
            element={
              <RequireAuth>
                <RoleGuard allowed={['suppohrt_admin']}>
                  <BeheerAuditLogPage />
                </RoleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/beheer/tijdregistratie"
            element={
              <RequireAuth>
                <ModuleGuard module="uren">
                  <RoleGuard allowed={SUPPOHRT_ROLES}>
                    <BeheerTijdregistratiePage />
                  </RoleGuard>
                </ModuleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/beheer/intervisie"
            element={
              <RequireAuth>
                <ModuleGuard module="intervisie">
                  <RoleGuard allowed={['suppohrt_admin']}>
                    <BeheerIntervisiePage />
                  </RoleGuard>
                </ModuleGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/beheer/e-learning"
            element={
              <RequireAuth>
                <ModuleGuard module="academy">
                  <RoleGuard allowed={['suppohrt_admin']}>
                    <BeheerElearningPage />
                  </RoleGuard>
                </ModuleGuard>
              </RequireAuth>
            }
          />

          <Route
            path="/profile"
            element={
              <RequireAuth>
                <ProfilePage />
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ShellProvider>
    </AuthProvider>
  );
}
